"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Phone, Mail, MessageSquare, Loader, ArrowRight, Sparkles } from "lucide-react";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in duration-500">
        <div className="size-16 rounded-lg bg-primary/20 flex items-center justify-center mb-4">
          <Sparkles className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground">Message Received!</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-8 border-primary/50 hover:bg-primary/10 rounded-lg">
          Send another message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-default">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className="bg-background/50 border-border/50 focus-visible:border-primary focus-visible:ring-primary/30 transition-all duration-300 rounded-lg" {...field} />
              </FormControl>
              <FormDescription className="text-[10px]">Please enter your full name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-default">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className="bg-background/50 border-border/50 focus-visible:border-primary focus-visible:ring-primary/30 transition-all duration-300 rounded-lg" {...field} />
              </FormControl>
              <FormDescription className="text-[10px]">We'll use this to get back to you.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-default">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+61 400 000 000" className="bg-background/50 border-border/50 focus-visible:border-primary focus-visible:ring-primary/30 transition-all duration-300 rounded-lg" {...field} />
              </FormControl>
              <FormDescription className="text-[10px]">Optional contact number.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="inquiry_type" rules={{
        required: "Please select an inquiry type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-default">Inquiry Type</FormLabel>
              <FormControl>
                <select className="flex h-9 w-full rounded-lg border border-border/50 bg-background/50 px-3 py-1 text-base shadow-sm transition-all duration-300 outline-none focus-visible:border-primary focus-visible:ring-[3px] focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm appearance-none" {...field}>
                  <option value="" disabled className="bg-background text-muted-foreground">Select an option</option>
                  <option value="Partnerships" className="bg-background text-foreground">Partnerships</option>
                  <option value="School Programs" className="bg-background text-foreground">School Programs</option>
                  <option value="Volunteering" className="bg-background text-foreground">Volunteering</option>
                  <option value="General Enquiry" className="bg-background text-foreground">General Enquiry</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-default">Your Message</FormLabel>
            <FormControl>
              <Textarea placeholder="How can we help you today?" className="min-h-[120px] bg-background/50 border-border/50 focus-visible:border-primary focus-visible:ring-primary/30 transition-all duration-300 resize-none rounded-lg" {...field} />
            </FormControl>
            <FormDescription className="text-[10px]">Tell us more about your interest.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto group bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 rounded-lg" size="lg">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Sending...
          </> : <>
            Send Message
            <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
          </>}
      </Button>
    </Form>;
}
export default function JoinOurMovement() {
  return <section data-section-id="250020" id="contact-details" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-lg bg-primary/10 blur-[120px]" />
        <div className="absolute top-[60%] -right-[10%] w-[40%] h-[60%] rounded-lg bg-secondary/10 blur-[100px]" />
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1682293/img/noise_texture.png')] opacity-[0.02] mix-blend-overlay" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-6xl font-default font-bold mb-4 tracking-tight">
            Let's Build the <span className="text-primary">Future Together</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-xl font-default">
            Get in touch to learn more about our programs, volunteering opportunities, or how to partner with us.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          
          {}
          <div className="lg:col-span-4 flex flex-col">
            <div className="bg-card/40 backdrop-blur-xl border border-border/50 rounded-3xl p-8 h-full flex flex-col justify-between shadow-xl relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative z-10">
                <h3 className="text-2xl font-default font-semibold mb-8 flex items-center gap-3">
                  <MessageSquare className="text-primary size-6" />
                  Contact Details
                </h3>
                
                <div className="space-y-8">
                  <div className="flex items-start gap-4">
                    <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Phone className="size-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1 font-default">Location</p>
                      <p className="text-lg font-medium font-default">Sydney, NSW, Australia</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Mail className="size-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1 font-default">Email Us</p>
                      <p className="text-xs sm:text-sm md:text-base font-medium font-default break-all overflow-hidden">futurereadyaustralia@gmail.com</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-8 border-t border-border/50 relative z-10">
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8">
            <div className="bg-card/40 backdrop-blur-xl border border-border/50 rounded-3xl p-8 md:p-12 shadow-xl h-full">
              <div className="mb-8">
                <h3 className="text-2xl font-default font-semibold mb-2">Send a Message</h3>
                <p className="text-muted-foreground font-default">Fill out the form below and our team will respond as soon as possible.</p>
              </div>
              
              <FormProvider formKey="join_movement_form_home" formId="join_movement_form_home" sectionName="VamosConversar" defaultValues={{
              name: "",
              email: "",
              phone: "",
              inquiry_type: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              inquiry_type: {
                required: true
              },
              message: {
                required: true
              }
            }} submitText="Send Message" successMessage="Thank you for reaching out! We've received your message and will get back to you shortly.">
                <ContactFormInner />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}